/**
 * probabilita — colore della percentuale "probabile titolare".
 *
 * Un solo posto per le soglie: campo, panchina, picker di Schiera e
 * anteprima admin dell'import leggono tutti da qui. Prima ogni pagina
 * aveva la sua copia e per spostare una soglia bisognava toccarne
 * quattro.
 *
 * Andrea 2026-09-11: verde dal 65% in su (era 75). Fantacalcio.it dà
 * 65-70 ai titolari "quasi sicuri" con un ballottaggio aperto, che in
 * giallo sembravano a rischio.
 */

import { tr } from "../../i18n";

export const SOGLIA_VERDE = 65;
export const SOGLIA_ROSSO = 40;

export type LivelloProbabilita = "out" | "rosso" | "giallo" | "verde";

export function livelloProbabilita(p: number): LivelloProbabilita {
  if (p <= 0) return "out";
  if (p < SOGLIA_ROSSO) return "rosso";
  if (p < SOGLIA_VERDE) return "giallo";
  return "verde";
}

/** `cls` = pastiglia trasparente (liste, sotto il nome);
 * `solid` = sfondo pieno, per stare sopra l'avatar nel campo. */
const STILE: Record<LivelloProbabilita, { cls: string; solid: string }> = {
  out: {
    cls: "bg-danger/40 text-fg border border-danger/60",
    solid: "bg-danger/70 text-white border border-danger/70",
  },
  rosso: {
    cls: "bg-danger/20 text-danger border border-danger/40",
    solid: "bg-danger/70 text-white border border-danger/70",
  },
  giallo: {
    cls: "bg-warning/25 text-warning border border-warning/40",
    solid: "bg-warning/70 text-ink border border-warning/70",
  },
  verde: {
    cls: "bg-success/25 text-success border border-success/40",
    solid: "bg-success/70 text-ink border border-success/70",
  },
};

/** Andrea 2026-10-23: squalificati e infortunati (dalle liste di
 * Fantacalcio.it o dalle parole chiave dell'import a mano) non hanno una
 * percentuale ma un badge: 🟥 Squalificato, ✚ Infortunato. La nota arriva
 * come "squalificato · motivo" / "infortunato · motivo"; "indisponibile"
 * (Sky) vale come infortunato. Chi e' "in dubbio" tiene la sua %. */
export type StatoIndisponibile = "squalificato" | "infortunato";

export function statoIndisponibile(note: string | null | undefined): StatoIndisponibile | null {
  const n = (note ?? "").trim().toLowerCase();
  if (n.startsWith("squalific")) return "squalificato";
  if (n.startsWith("infortun") || n.startsWith("indisponib")) return "infortunato";
  return null;
}

const ETICHETTA_STATO: Record<StatoIndisponibile, string> = {
  squalificato: "🟥 Squalificato",
  infortunato: "✚ Infortunato",
};

/** Testo per il fumetto al tocco (sul telefono il `title` non si vede):
 * "✚ Infortunato — noie muscolari, out contro il Monza" oppure
 * "90% di giocare (Fantacalcio.it)". */
export function testoInfoProbabilita(
  p: number,
  note: string | null | undefined,
  fonte: string,
): string {
  const stato = statoIndisponibile(note);
  if (stato) {
    const motivo = (note ?? "").split("·").slice(1).join("·").trim();
    return tr(ETICHETTA_STATO[stato]) + (motivo ? ` — ${motivo}` : "");
  }
  // Andrea 2026-10-23: niente fonte nel testo
  void fonte;
  const extra = note ? ` (${note})` : "";
  return tr("{{v1}}% di giocare", { v1: p }) + extra;
}

export function badgeProbabilita(
  p: number,
  note?: string | null,
): {
  label: string;
  /** Solo il simbolo (🟥 / ✚) per squalificati e infortunati. */
  icona?: string;
  livello: LivelloProbabilita;
  cls: string;
  solid: string;
} {
  const stato = statoIndisponibile(note);
  if (stato) {
    // `icona` = solo il simbolo, per il campo (Andrea 2026-10-23: variante D,
    // in basso a destra al posto della %, la parola nel tooltip).
    return {
      label: tr(ETICHETTA_STATO[stato]),
      icona: stato === "squalificato" ? "🟥" : "✚",
      livello: "out",
      ...STILE.out,
    };
  }
  const livello = livelloProbabilita(p);
  return {
    label: livello === "out" ? "OUT" : `${p}%`,
    livello,
    ...STILE[livello],
  };
}
